import React from 'react';
import { useAuth } from './auth-guard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Music, User, Shield } from 'lucide-react';

interface AnnotationAuthGuardProps {
  children: React.ReactNode;
  requireParticipant?: boolean;
  fallback?: React.ReactNode;
}

export function AnnotationAuthGuard({ 
  children, 
  requireParticipant = false,
  fallback 
}: AnnotationAuthGuardProps) {
  const { 
    user, 
    isLoading, 
    participantProfile, 
    isLoadingParticipant,
    checkParticipantProfile 
  } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return fallback || <AuthLoadingScreen message="Checking authentication..." />;
  }

  // Require authentication first
  if (!user) {
    return <AuthRequired />;
  }

  // Show loading state while checking participant profile
  if (isLoadingParticipant) {
    return fallback || <AuthLoadingScreen message="Checking participant profile..." />;
  }

  // If participant profile is required but doesn't exist
  if (requireParticipant && (!participantProfile || !participantProfile.hasProfile)) {
    return <ParticipantProfileRequired onRetry={checkParticipantProfile} />;
  }

  // All checks passed, render children
  return <>{children}</>;
}

function AuthLoadingScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        <p className="text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

function AuthRequired() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
            <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle>Authentication Required</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            You need to be signed in to access the music annotation research platform.
          </p>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Please sign in with your WebAuthn credentials to continue.
            </p>
          </div>
          <Button onClick={() => window.location.reload()} className="w-full">
            Go to Sign In
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ParticipantProfileRequired({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
            <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <CardTitle>Participant Profile Required</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            You need to complete your participant profile to access this feature.
          </p>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              The annotation platform requires demographic information for research purposes.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={onRetry} variant="outline" className="flex-1">
              Check Again
            </Button>
            <Button onClick={() => window.location.reload()} className="flex-1">
              Go Back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Higher-order component for annotation platform routes
export function withAnnotationAuth<P extends object>(
  Component: React.ComponentType<P>,
  requireParticipant: boolean = false
) {
  return function AnnotationAuthenticatedComponent(props: P) {
    return (
      <AnnotationAuthGuard requireParticipant={requireParticipant}>
        <Component {...props} />
      </AnnotationAuthGuard>
    );
  };
}

// Hook for annotation platform navigation logic
export function useAnnotationNavigation() {
  const { user, participantProfile, isLoadingParticipant } = useAuth();

  const canAccessAnnotation = () => {
    return !!user && !isLoadingParticipant;
  };

  const hasParticipantProfile = () => {
    return participantProfile?.hasProfile === true;
  };

  const needsParticipantProfile = () => {
    return !!user && !isLoadingParticipant && !hasParticipantProfile();
  };

  const getAnnotationRoute = () => {
    if (!canAccessAnnotation()) {
      return 'auth-required';
    }
    
    if (needsParticipantProfile()) {
      return 'demographics';
    }
    
    return 'annotation';
  };

  return {
    canAccessAnnotation,
    hasParticipantProfile,
    needsParticipantProfile,
    getAnnotationRoute,
    isLoading: isLoadingParticipant
  };
}